import React from 'react'

import { Typography } from '@telavita-core/react-design-kit'

import ModalGeneric from '../ModalGeneric'
import { ExplanationItem } from './components/ExplanationItem'

import * as S from './styles'

interface ExplanationOfSchedulesStatusesModalProps {
  onClose: () => void
}

enum StatusesColors {
  SCHEDULED = '#1F35B5',
  ATTENDED = '#4AC326',
  UNATTENDED = '#F9BD35',
  PATIENT_NO_SHOW = '#FD4949',
  PROFESSIONAL_NO_SHOW = '#BF1D1D',
  CANCELED = '#898989',
  RETURNED = '#D5D5D5',
  SCHEDULE_NO_PARTICIPANTS = '#ED60FC'
}

const explanations = [
  {
    title: 'Agendadas',
    color: StatusesColors.SCHEDULED,
    description: (
      <div>
        <Typography variant="content2">
          São as consultas marcadas pelos pacientes mediante as disponibilidades determinadas por você. 
        </Typography>

        <S.DescriptionText variant="content2">
          Este status é útil para visualizar quantas consultas agendadas você possui em uma data futura. 
          Para utilizar dessa forma, selecione um período personalizado no filtro.
        </S.DescriptionText>
      </div>
    )
  },

  {
    title: 'Realizadas',
    color: StatusesColors.ATTENDED,
    description: (
      <Typography variant="content2">
        São as consultas efetuadas com os pacientes.
      </Typography>
    )
  },

  {
    title: 'Não realizadas',
    color: StatusesColors.UNATTENDED,
    description: (
      <>
        <Typography variant="content2">
          É quando você e o paciente entram na sala virtual em momentos diferentes, sem que tenham se encontrado. 
        </Typography>
        <br />
        <Typography variant="content2">
          <b>Por exemplo:</b> você entrou às 19h00, esperou até 19h20 mas o paciente não apareceu. 
          Então você sai da sala, presumindo que o paciente faltou. Mas o paciente, atrasado, entra às 19h22, 
          só que você não está mais na sala. Se o oposto ocorrer (o paciente entrar primeiro, sair, e você entrar depois), 
          a consulta também será dada como “não realizada”.
        </Typography>
      </>
    )
  },

  {
    title: 'Ambos faltaram',
    color: StatusesColors.SCHEDULE_NO_PARTICIPANTS,
    description: (
      <>
        <Typography variant="content2">
          É quando nem você e nem o paciente entram na sala virtual da consulta.
        </Typography>
        <br />
        <Typography variant="content2">
          <b>Importante:</b> caso vocês combinem previamente que a consulta não vai ocorrer, 
          procure fazer com que ela seja reagendada ou cancelada, para evitar a atribuição deste status.
        </Typography>
      </>
    )
  },

  {
    title: 'Paciente faltou',
    color: StatusesColors.PATIENT_NO_SHOW,
    description: (
      <Typography variant="content2">
        É quando o paciente não comparece à consulta.
      </Typography>
    )
  },

  {
    title: 'Eu faltei',
    color: StatusesColors.PROFESSIONAL_NO_SHOW,
    description: (
      <Typography variant="content2">
        É quando você não comparece à consulta.
      </Typography>
    )
  },

  {
    title: 'Canceladas',
    color: StatusesColors.CANCELED,
    description: (
      <div>
        <S.DescriptionText>
          É quando um paciente cancela uma consulta que estava agendada com você.
        </S.DescriptionText>

        <S.DescriptionText>
          O paciente só pode cancelar uma consulta com, no mínimo, 24h de antecedência do horário agendado. 
        </S.DescriptionText>

        <S.DescriptionText>
          Você também pode cancelar uma consulta com um paciente em caso de urgência ou imprevistos, 
          mas para isso, é preciso entrar em contato com a equipe de atendimento da Telavita.
        </S.DescriptionText>
      </div>
    )
  },

  {
    title: 'Devolvidas',
    color: StatusesColors.RETURNED,
    description: (
      <Typography variant='content2'>
        É um status atribuído pela equipe de atendimento da Telavita quando é identificado 
        algum erro que justifica o paciente obter de volta o direito de um novo agendamento.
      </Typography>
    )
  }
]

export const ExplanationOfSchedulesStatusesModal: React.FC<ExplanationOfSchedulesStatusesModalProps> = ({
  onClose,
}) => (
  <ModalGeneric
    modalTitle='Significado dos status de consultas'
    hasButton
    btnText='ENTENDI'
    onCloseModal={onClose}
    btnOnClick={onClose}
  > 
    <S.Container>
      {explanations.map(({title, description, color}) => (
        <ExplanationItem
          key={title}
          title={title}
          description={description}
          color={color}
        />
      ))}
    </S.Container>
  </ModalGeneric>
)